import { ScheduleItem } from "./types";

/**
 * 生成预排决策的交互式 HTML 报表
 * 包含内嵌的甘特图和列表视图，可脱机使用
 */
export const handleExportDecisionHTML = (
  generatedSeries: ScheduleItem[],
  teacherCapability: Record<string, boolean>
) => {
  // 调试输出
  console.log('🔍 导出数据检查:', {
    数据总数: generatedSeries.length,
    老师数量: Object.keys(teacherCapability).length,
    前3条数据: generatedSeries.slice(0, 3)
  });
  
  if (generatedSeries.length === 0) {
    alert('⚠️ 无可导出数据！请先上传排班数据或生成预排方案。');
    return;
  }
  
  const reportDate = new Date().toLocaleDateString('zh-CN');
  
  // 寻找最小日期作为时间轴起点
  let minTs = Date.now();
  if (generatedSeries.length > 0) {
      minTs = generatedSeries.reduce((min, item) => Math.min(min, item.startDate.getTime()), Infinity);
  }
  // 缓冲：从最早排班前2天开始
  const baseDate = new Date(minTs);
  baseDate.setDate(baseDate.getDate() - 2);
  baseDate.setHours(0, 0, 0, 0); // 标准化为本地午夜
  const baseDateTs = baseDate.getTime();

  // 获取排班涉及的唯一老师列表
  const uniqueTeachers = Array.from(new Set(generatedSeries.map(i => i.teacher))).sort();

  // 处理排班数据，添加策略标签并保留必要字段（供导出甘特使用）
  const assignments = generatedSeries.map((item, index) => {
      let strategy = '顺接';
      let nature = 'natural'; // natural, axis, idle
      const cap = teacherCapability[item.teacher];
      const is21 = item.type === '21Day';
      if (is21 && cap) {
          strategy = '轴接';
          nature = 'axis';
      } else {
          strategy = '顺接';
          nature = 'natural';
      }

      // 确保 startDate 是 Date 对象
      const startDate = item.startDate instanceof Date ? item.startDate : new Date(item.startDate);
      
      // 调试单条数据
      if (index === 0) {
          console.log('📝 首条数据转换:', {
              原始startDate: item.startDate,
              转换后: startDate,
              ISO字符串: startDate.toISOString(),
              老师: item.teacher,
              类型: item.type
          });
      }

      return {
          id: item.id,
          // 保留原始 startDate ISO 以便客户端渲染
          date: startDate.toISOString(),
          dateDisplay: `${startDate.getMonth() + 1}.${startDate.getDate()}`,
          teacher: item.teacher,
          type: item.type,
          strategy: strategy,
          nature: nature,
          duration: is21 ? 27 : 21,
          groupCode: item.groupCode || '',
          studentCount: item.studentCount || '',
          isPredicted: !!item.isPredicted,
          sourceSheet: item.sourceSheet || '',
          raw: null // 不序列化 raw 对象，避免循环引用
      };
  });
  
  console.log('✅ assignments 数组生成完成:', {
    总数: assignments.length,
    首条: assignments[0]
  });

  // 在生成 HTML 前先验证 JSON 序列化
  let assignmentsJson = '';
  try {
    assignmentsJson = JSON.stringify(assignments);
    
    console.log('✅ JSON 序列化成功:', {
      字符长度: assignmentsJson.length,
      前100字符: assignmentsJson.substring(0, 100)
    });
  } catch (error) {
    console.error('❌ JSON 序列化失败:', error);
    alert('数据序列化失败，无法导出！错误：' + (error as Error).message);
    return;
  }

    const htmlContent = `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>排班预排决策交互报表 - ${reportDate}</title>
  <script src="https://cdn.tailwindcss.com"><\/script>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <!-- 数据存储在单独的script标签中，避免模板字符串问题 -->
  <script id="schedule-data" type="application/json">${assignmentsJson.replace(/</g, '\\u003c').replace(/>/g, '\\u003e')}<\/script>
  <style>
    body { font-family: 'Inter', sans-serif; background: #f8fafc; color: #1e293b; overflow: hidden; height: 100vh; display: flex; flex-direction: column; }
    .glass { background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(10px); }
    
    /* Badges */
    .badge-21 { background: #eff6ff; color: #3b82f6; border: 1px solid #dbeafe; }
    .badge-15 { background: #ecfdf5; color: #10b981; border: 1px solid #d1fae5; }
    
    .st-axis { color: #6366f1; background: #eef2ff; border: 1px solid #e0e7ff; }
    .st-natural { color: #059669; background: #ecfdf5; border: 1px solid #d1fae5; }
    
    /* Tabs */
    .tab-active { border-bottom: 2px solid #4f46e5; color: #4f46e5; font-weight: 600; }
    .tab-inactive { color: #64748b; hover:text-slate-800; cursor: pointer; }
    
    /* Gantt Specific */
    .gantt-container { overflow: auto; height: 100%; position: relative; user-select: none; background: white; }
    .gantt-header { position: sticky; top: 0; z-index: 20; background: #fff; display: flex; border-bottom: 1px solid #e2e8f0; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
    .gantt-row { display: flex; border-bottom: 1px solid #f1f5f9; position: relative; transition: background 0.1s; }
    .gantt-row:hover { background: #f8fafc; }
    .day-col { width: 40px; flex-shrink: 0; border-right: 1px solid #f1f5f9; text-align: center; font-size: 10px; color: #94a3b8; display: flex; flex-direction: column; justify-content: center; height: 100%; }
    .day-col.weekend { background: #f8fafc; }
    .day-col.today { background: #fff1f2; color: #be123c; font-weight: bold; border-left: 1px solid #fecdd3; border-right: 1px solid #fecdd3; }
    
        .g-bar { 
            position: absolute; height: 48px; border-radius: 8px; 
            display: grid; grid-template-columns: 1fr auto; 
            padding: 0; 
            font-size: 11px; font-weight: 600; color: white; cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.06);
            transition: box-shadow 0.1s, transform 0.1s;
            overflow: hidden;
            margin-top: 12px;
        }
        .g-bar:hover { z-index: 30; filter: brightness(102%); }
        .g-bar.predicted { border: 1px dashed rgba(148,163,184,0.9); opacity:0.95; }
        .g-bar.uploaded { border: 1px solid rgba(0,0,0,0.06); box-shadow: 0 2px 6px rgba(2,6,23,0.06); }
        .g-bar .pred-overlay { position:absolute; inset:0; background-image: repeating-linear-gradient(45deg, rgba(0,0,0,0.03) 0px, rgba(0,0,0,0.03) 4px, transparent 4px, transparent 8px); pointer-events:none; opacity:0.18; z-index: 1; }
        .g-bar.selected { box-shadow: 0 6px 18px rgba(99,102,241,0.18); transform: translateY(-3px); }
        .list-item.selected { box-shadow: 0 6px 18px rgba(99,102,241,0.08); border-color: rgba(99,102,241,0.15); }
        
        /* 排班条右侧信息区 */
        .bar-info { 
            display: flex; flex-direction: column; justify-content: center; align-items: flex-end;
            padding: 0 10px; gap: 3px; width: 100px; flex-shrink: 0; z-index: 2; position: relative;
            background: rgba(0,0,0,0.04); border-left: 1px solid rgba(255,255,255,0.3);
            transition: all 0.2s;
        }
        .g-bar:hover .bar-info { background: rgba(0,0,0,0.08); }
        .bar-info .group-code { font-size: 12px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 80px; }
        .bar-info .type-badge { font-size: 10px; padding: 2px 6px; border-radius: 4px; background: rgba(255,255,255,0.25); white-space: nowrap; }
        .bar-info .extra-info { font-size: 9px; opacity: 0; max-height: 0; overflow: hidden; transition: all 0.2s; }
        .g-bar:hover .bar-info .extra-info { opacity: 0.9; max-height: 20px; }
        
        /* 搜索框样式 */
        .search-box { 
            position: fixed; top: 80px; right: 24px; z-index: 40;
            background: rgba(255,255,255,0.98); backdrop-filter: blur(10px);
            border-radius: 12px; padding: 8px 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 8px;
            width: 280px; transition: all 0.2s;
        }
        .search-box:focus-within { box-shadow: 0 6px 16px rgba(99,102,241,0.15); border-color: #818cf8; }
        .search-box input { 
            flex: 1; border: none; outline: none; background: transparent;
            font-size: 13px; color: #1e293b; font-weight: 500;
        }
        .search-box input::placeholder { color: #94a3b8; }
        .search-box .search-icon { width: 16px; height: 16px; color: #94a3b8; flex-shrink: 0; }
        .search-box .clear-btn { 
            display: none; width: 20px; height: 20px; border-radius: 50%; 
            background: #e2e8f0; color: #64748b; border: none; cursor: pointer;
            font-size: 14px; line-height: 1; transition: all 0.15s; flex-shrink: 0;
        }
        .search-box .clear-btn:hover { background: #cbd5e1; }
        .search-box.has-value .clear-btn { display: flex; align-items: center; justify-content: center; }
        .search-box .result-count { font-size: 11px; color: #6366f1; font-weight: 600; white-space: nowrap; }
        
        /* 搜索高亮效果 */
        .gantt-row.filtered-out { opacity: 0.15; pointer-events: none; transition: opacity 0.3s; }
        .teacher-name-highlight { background: #fef08a; padding: 2px 4px; border-radius: 3px; font-weight: 700; }
        .g-bar.search-matched { animation: pulse-border 1.5s ease-in-out infinite; }
        @keyframes pulse-border { 0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.7); } 50% { box-shadow: 0 0 0 4px rgba(99,102,241,0); } }
    
    /* kept for backward compatibility */
    .bg-21 { background: #3b82f6; border: 1px solid #2563eb; }
    .bg-15 { background: #10b981; border: 1px solid #059669; }

    /* Drag Tooltip */
    #drag-tooltip {
      position: absolute; top: -35px; left: 50%; transform: translateX(-50%);
      background: #1e293b; color: white; padding: 6px 10px; border-radius: 6px;
      font-size: 11px; font-weight: bold; pointer-events: none; opacity: 0;
      transition: opacity 0.1s; white-space: nowrap; z-index: 200; box-shadow: 0 4px 6px rgba(0,0,0,0.2);
    }
    .g-bar:active #drag-tooltip { opacity: 1; }

    /* Add Modal */
    .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: none; align-items: center; justify-content: center; backdrop-filter: blur(2px); }
    .modal-content { background: white; padding: 24px; border-radius: 12px; width: 320px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
  </style>
</head>
<body>
  <div class="glass flex-none border-b border-slate-200 px-6 py-4 flex justify-between items-center shadow-sm z-50 relative">
          <div class="flex items-center gap-6">
          <h1 class="text-xl font-bold text-slate-800 flex items-center gap-2">
              📅 预排决策交互报表
              <span class="text-xs font-normal text-slate-500 bg-slate-100 px-2 py-1 rounded-full border border-slate-200">${reportDate}</span>
          </h1>
          <div class="flex gap-4 ml-8">
              <button onclick="switchTab('gantt')" id="tab-gantt" class="tab-active px-2 py-1 text-sm transition-colors">甘特图看板</button>
              <button onclick="switchTab('list')" id="tab-list" class="tab-inactive px-2 py-1 text-sm transition-colors">排班列表管理</button>
          </div>
      </div>
          <div id="legend" style="display:flex;gap:12px;align-items:center"></div>
      <div class="flex items-center gap-4">
          <div class="text-sm font-medium text-slate-600">排班: <span id="total-count" class="text-indigo-600 font-bold">0</span></div>
          <button onclick="openAddModal()" class="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1 transition-colors shadow-sm">
            <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"></path></svg>
            新增
          </button>
      </div>
  </div>

  <div class="flex-1 overflow-hidden relative bg-slate-50">
      
      <div id="view-gantt" class="absolute inset-0 overflow-hidden bg-white flex flex-col transition-opacity duration-200">
          <!-- 搜索框 -->
          <div id="teacher-search" class="search-box">
              <svg class="search-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input type="text" id="search-input" placeholder="搜索老师..." />
              <button class="clear-btn" onclick="clearSearch()">×</button>
              <span id="search-result-count" class="result-count" style="display:none;"></span>
          </div>
          <div id="gantt-canvas" class="gantt-container"></div>
      </div>

      <div id="view-list" class="absolute inset-0 overflow-y-auto p-8 max-w-7xl mx-auto w-full transition-opacity duration-200 opacity-0 pointer-events-none">
          <div id="list-container"></div>
      </div>

  </div>

  <div id="add-modal" class="modal-overlay">
      <div class="modal-content">
          <h3 class="text-lg font-bold mb-4 text-slate-800">新增排班</h3>
          <div class="space-y-4">
              <div>
                  <label class="block text-xs font-bold text-slate-500 mb-1">老师姓名</label>
                  <select id="input-teacher" class="w-full border border-slate-300 rounded p-2 text-sm">
                      <!-- Filled by JS -->
                  </select>
              </div>
              <div>
                  <label class="block text-xs font-bold text-slate-500 mb-1">班型</label>
                  <select id="input-type" class="w-full border border-slate-300 rounded p-2 text-sm">
                      <option value="21Day">21天训练营</option>
                      <option value="15Day">15天/闲鱼/转介绍</option>
                  </select>
              </div>
              <div>
                  <label class="block text-xs font-bold text-slate-500 mb-1">开营日期</label>
                  <input type="date" id="input-date" class="w-full border border-slate-300 rounded p-2 text-sm">
              </div>
              <div class="flex justify-end gap-2 pt-2">
                  <button onclick="closeAddModal()" class="px-3 py-1.5 text-slate-500 text-sm hover:bg-slate-100 rounded">取消</button>
                  <button onclick="commitAdd()" class="px-3 py-1.5 bg-indigo-600 text-white text-sm font-bold rounded hover:bg-indigo-700">确认添加</button>
              </div>
          </div>
      </div>
  </div>

  <script>
            // --- Config ---
            const CELL_WIDTH = 40; // match GanttView
            
            // 注入服务端导出时的日期（固定时间锚点），避免报表在未来打开时时间轴错乱
            const TODAY = new Date(${JSON.stringify(Date.now())}); 
            TODAY.setHours(0,0,0,0);
            
            // 时间范围：始终从导出日期的前 15 天开始（隐藏过于久远的历史数据）
            const MIN_DATE = new Date(TODAY);
            MIN_DATE.setDate(MIN_DATE.getDate() - 15);

            // 注入老师列表 (JSON Safe)
            const TEACHERS_LIST = ${JSON.stringify(uniqueTeachers)};

              // 使用 JSON.stringify 安全注入配置对象，避免手写字面量的语法错误风险
              const TYPE_COLORS = ${JSON.stringify({
                  '21Day': { bg: 'bg-blue-500', border: 'border-blue-600', text: 'text-blue-600', label: '21天', hex: '#3b82f6' },
                  '15Day': { bg: 'bg-emerald-500', border: 'border-emerald-600', text: 'text-emerald-600', label: '15天', hex: '#10b981' },
                  'Referral': { bg: 'bg-purple-500', border: 'border-purple-600', text: 'text-purple-600', label: '转介绍', hex: '#a855f7' },
                  'Xianyu': { bg: 'bg-amber-500', border: 'border-amber-600', text: 'text-amber-600', label: '闲鱼', hex: '#f59e0b' },
              })};

                    // Populate a small legend into header
                    function populateLegend() {
                        const el = document.getElementById('legend');
                        if(!el) return;
                        const typeItems = Object.keys(TYPE_COLORS).map(k => {
                            const c = TYPE_COLORS[k];
                            return '<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:#475569">' +
                                   '<div style="width:12px;height:12px;border-radius:2px;background:' + c.hex + ';border:1px solid rgba(0,0,0,0.06)"></div>' +
                                   '<div style="white-space:nowrap">' + c.label + '</div></div>';
                        }).join('');
                        const spacer = '<div style="width:12px"></div>';
                        const uploadedItem = '<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:#475569">' +
                                            '<div style="width:24px;height:8px;border-radius:3px;background:#e6eefb;border:1px solid #dbeafe"></div>' +
                                            '<div>已上传</div></div>';
                        const predictedItem = '<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:#475569">' +
                                             '<div style="width:24px;height:8px;border-radius:3px;background:repeating-linear-gradient(45deg,#dbeafe 0 4px, rgba(0,0,0,0) 4px 8px);border:1px solid #dbeafe"></div>' +
                                             '<div>预测</div></div>';
                        el.innerHTML = typeItems + spacer + uploadedItem + predictedItem;
                    }

                    function initTeachers() {
                        const sel = document.getElementById('input-teacher');
                        if(sel && typeof TEACHERS_LIST !== 'undefined' && Array.isArray(TEACHERS_LIST)) {
                            sel.innerHTML = TEACHERS_LIST.map(t => {
                                return '<option value="' + t + '">' + t + '</option>';
                            }).join('');
                        }
                    }

                    document.addEventListener('DOMContentLoaded', () => {
                        populateLegend();
                        initTeachers();
                    });

            // getDayConfig: ported from utils.ts to render per-day operation nodes (no template literals)
            function getDayConfig(type, offset, isTentative, isFuture) {
                var label = '';
                var title = '';
                var role = 'run';
                var dDay = offset - 3;
                if (offset < 3) {
                    role = 'prep';
                    label = String(offset + 1);
                    title = '承接期 Day ' + String(offset + 1);
                } else if (offset === 3) {
                    role = 'open'; label = '开'; title = '开营日';
                } else {
                    if (type === '21Day') {
                        if (dDay <= 21) {
                            if (dDay === 21) { role = 'end'; label = '结'; title = '结营日'; }
                            else if ([6, 11, 17].indexOf(dDay) !== -1) { role = 'rest'; label = String(dDay); title = '休息日'; }
                            else if (dDay === 14) { role = 'conversion'; label = String(dDay); title = '一转节点'; }
                            else { label = String(dDay); title = '运营期 Day ' + String(dDay); }
                        } else { role = 'cut'; label = '截'; title = '截单期'; }
                    } else {
                        if (dDay <= 15) {
                            if (dDay === 15) { role = 'end'; label = '结'; title = '结营日'; }
                            else if ([6, 12].indexOf(dDay) !== -1) { role = 'rest'; label = String(dDay); title = '休息日'; }
                            else if (dDay === 8) { role = 'conversion'; label = String(dDay); title = '一转节点'; }
                            else { label = String(dDay); title = '运营期 Day ' + String(dDay); }
                        } else { role = 'cut'; label = '截'; title = '截单期'; }
                    }
                }

                var style = '';
                var BASE_BG = isFuture ? 'bg-purple-50' : 'bg-white';
                var BASE_BORDER = isFuture ? 'border-r border-purple-200' : 'border-r border-slate-100';

                if (isTentative) {
                    var PRED_BORDER = 'border-r border-dashed border-slate-300';
                    if (role === 'open') style = 'bg-indigo-50 text-indigo-800 font-bold ' + PRED_BORDER;
                    else if (role === 'prep') style = 'bg-slate-50 text-slate-400 ' + PRED_BORDER;
                    else if (role === 'cut') style = 'bg-red-50 text-red-900 ' + PRED_BORDER;
                    else style = 'bg-white text-indigo-400 ' + PRED_BORDER;
                } else {
                    var textBase = (TYPE_COLORS[type] && TYPE_COLORS[type].text) ? TYPE_COLORS[type].text : 'text-gray-600';
                    if (role === 'prep') style = BASE_BG + ' text-slate-300 ' + BASE_BORDER;
                    else if (role === 'open') style = BASE_BG + ' text-yellow-600 font-bold ' + BASE_BORDER;
                    else if (role === 'cut') style = BASE_BG + ' text-red-400 ' + BASE_BORDER;
                    else if (role === 'rest') style = BASE_BG + ' ' + textBase + ' opacity-50 ' + BASE_BORDER;
                    else if (role === 'conversion') style = BASE_BG + ' ' + textBase + ' font-bold underline decoration-yellow-400 underline-offset-2 ' + BASE_BORDER;
                    else if (role === 'end') style = BASE_BG + ' text-red-600 font-bold ' + BASE_BORDER;
                    else style = BASE_BG + ' ' + textBase + ' ' + BASE_BORDER;
                }
                return { label: label, style: style, title: title };
            }

      // --- Data Model ---
      // 从 script 标签读取 JSON 数据（最可靠的方式）
      const rawDataJson = (function() {
          try {
              const dataScript = document.getElementById('schedule-data');
              if (!dataScript) {
                  console.error('❌ 找不到数据script标签');
                  return [];
              }
              const jsonStr = dataScript.textContent || '';
              console.log('✅ 读取到JSON数据，长度:', jsonStr.length);
              return JSON.parse(jsonStr);
          } catch(e) {
              console.error('❌ 数据解析失败:', e);
              alert('数据解析失败: ' + e.message);
              return [];
          }
      })();
      
      // 立即验证数据
      console.log('📦 HTML中接收到的原始数据:', {
        类型: typeof rawDataJson,
        是否是数组: Array.isArray(rawDataJson),
        长度: rawDataJson ? rawDataJson.length : 0,
        首条: rawDataJson && rawDataJson[0] ? rawDataJson[0] : null
      });
      
      if (!rawDataJson || !Array.isArray(rawDataJson) || rawDataJson.length === 0) {
        console.error('❌ 数据为空或格式错误！');
        alert('数据加载失败：接收到的数据为空或格式错误');
      }
      
      let scheduleData = rawDataJson.map(d => {
          const dateObj = new Date(d.date);
          dateObj.setHours(0,0,0,0);
          return {
              ...d, 
              dateObj: dateObj,
              endTime: new Date(dateObj.getTime() + (d.duration * 24 * 3600 * 1000))
          };
      });

      // 调试输出
      console.log('📊 HTML 内数据初始化:', {
        原始数据条数: rawDataJson.length,
        解析后条数: scheduleData.length,
        前2条: scheduleData.slice(0, 2)
      });

      // --- State ---
      let currentTab = 'gantt';
      
      // --- Helper Functions ---
      function getLabel(type) {
          if(type==='21Day') return '21天';
          if(type.includes('15') || type.includes('Xianyu')) return '15天组';
          return type;
      }

      function formatDate(d) {
          return (d.getMonth()+1) + '.' + d.getDate();
      }

      function calculateLayout() {
          const teachers = [...new Set(scheduleData.map(i => i.teacher))].sort();
          return teachers.map(teacher => {
              const items = scheduleData.filter(i => i.teacher === teacher)
                  .sort((a, b) => a.dateObj - b.dateObj);
              const lanes = [];
              items.forEach(item => {
                  let placed = false;
                  for(let i=0; i<lanes.length; i++) {
                      const lastItem = lanes[i][lanes[i].length -1];
                      if(item.dateObj > lastItem.endTime) {
                          lanes[i].push(item);
                          placed = true;
                          break;
                      }
                  }
                  if(!placed) lanes.push([item]);
              });
              return { teacher, lanes };
          });
      }

      // --- Rendering Logic ---
      function render() {
          document.getElementById('total-count').innerText = scheduleData.length;
          if(currentTab === 'gantt') {
              renderGantt();
              // 初始化搜索功能（只在甘特图视图）
              setTimeout(() => initSearch(), 100);
          } else {
              renderList();
          }
      }

      function switchTab(tab) {
          currentTab = tab;
          document.getElementById('tab-list').className = tab === 'list' ? 'tab-active px-2 py-1 text-sm' : 'tab-inactive px-2 py-1 text-sm';
          document.getElementById('tab-gantt').className = tab === 'gantt' ? 'tab-active px-2 py-1 text-sm' : 'tab-inactive px-2 py-1 text-sm';
          
          const vList = document.getElementById('view-list');
          const vGantt = document.getElementById('view-gantt');
          const searchBox = document.getElementById('teacher-search');
          
          if(tab === 'list') {
              vList.style.opacity = 1; vList.style.pointerEvents = 'auto';
              vGantt.style.opacity = 0; vGantt.style.pointerEvents = 'none';
              if(searchBox) searchBox.style.display = 'none';
              renderList();
          } else {
              vList.style.opacity = 0; vList.style.pointerEvents = 'none';
              vGantt.style.opacity = 1; vGantt.style.pointerEvents = 'auto';
              if(searchBox) searchBox.style.display = 'flex';
              renderGantt();
          }
      }
      
      function deleteItem(id) {
          if(!confirm('确定要删除这条排班吗？')) return;
          scheduleData = scheduleData.filter(i => i.id !== id);
          render();
      }

      function openAddModal() {
          const d = new Date();
          d.setDate(d.getDate() + 1);
          document.getElementById('input-date').valueAsDate = d;
          document.getElementById('add-modal').style.display = 'flex';
      }

      function closeAddModal() {
          document.getElementById('add-modal').style.display = 'none';
      }

      function commitAdd() {
          const teacher = document.getElementById('input-teacher').value;
          const type = document.getElementById('input-type').value;
          const dateVal = document.getElementById('input-date').value;
          
          if(!teacher || !dateVal) { alert('请填写完整信息'); return; }
          
          const dateObj = new Date(dateVal);
          dateObj.setHours(0,0,0,0);
          
          const newItem = {
              id: 'manual-' + Date.now(),
              teacher,
              type,
              strategy: '手动',
              nature: 'manual',
              duration: type === '21Day' ? 27 : 21,
              date: dateObj.toISOString(),
              dateObj: dateObj,
              endTime: new Date(dateObj.getTime() + ((type === '21Day' ? 27 : 21) * 24 * 3600 * 1000))
          };
          
          scheduleData.push(newItem);
          closeAddModal();
          render();
      }

      // 辅助函数：创建日期分组头部
      function createDateHeader(dateObj, itemCount) {
          const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
          const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
          const dateStr = (dateObj.getMonth() + 1) + '月' + dateObj.getDate() + '日 · ' + weekDays[dateObj.getDay()];
          const dotClass = isWeekend ? 'bg-red-500' : 'bg-indigo-500';
          const textClass = isWeekend ? 'text-red-600' : 'text-slate-700';
          
          return '<div class="flex items-center justify-between mb-4 sticky top-0 z-10 py-2 bg-slate-50/90 backdrop-blur-sm border-b border-slate-200">' +
                 '<div class="flex items-center gap-3">' +
                 '<div class="w-2.5 h-2.5 rounded-full ' + dotClass + '"></div>' +
                 '<h2 class="text-lg font-bold ' + textClass + '">' + dateStr + '</h2></div>' +
                 '<span class="text-xs font-bold bg-white px-3 py-1 rounded-full border border-slate-200 text-slate-500">' + itemCount + ' 班次</span></div>';
      }
      
      // 辅助函数：创建单个排班卡片
      function createListItemCard(item) {
          const typeClass = item.type === '21Day' ? 'bg-blue-500' : 'bg-emerald-500';
          const badgeClass = item.type === '21Day' ? 'badge-21' : 'badge-15';
          
          // 只显示轴接标签（高亮显示），忽略顺接
          const strategyBadge = item.nature === 'axis' 
              ? '<span class="text-xs font-bold px-3 py-1 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md">⚡ 轴接</span>' 
              : '';
          
          return '<div id="list-' + item.id + '" onclick="selectItem(&quot;' + item.id + '&quot;)" class="list-item bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all group relative overflow-hidden" style="cursor:pointer">' +
                 '<div class="absolute left-0 top-0 bottom-0 w-1.5 ' + typeClass + '"></div>' +
                 '<div class="flex justify-between items-start">' +
                 '<div><div class="flex items-center gap-2 mb-2">' +
                 '<span class="text-xs font-bold px-2 py-0.5 rounded ' + badgeClass + '">' + getLabel(item.type) + '</span>' +
                 strategyBadge + '</div>' +
                 '<div class="font-bold text-slate-800 text-lg">' + item.teacher + '</div></div>' +
                 '<button onclick="deleteItem(&quot;' + item.id + '&quot;)" class="text-slate-300 hover:text-red-500 p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="删除排班">' +
                 '<svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>' +
                 '</button></div></div>';
      }

      function renderList() {
          const container = document.getElementById('list-container');
          if (scheduleData.length === 0) {
              container.innerHTML = '<div class="text-center text-slate-400 py-20 italic">暂无排班数据</div>';
              return;
          }
          
          // 按日期分组，只显示今日及以后的排班
          const groups = {};
          scheduleData.forEach(item => {
              if (item.dateObj >= TODAY) {
                  const k = item.dateObj.toISOString();
                  (groups[k] = groups[k] || []).push(item);
              }
          });

          container.innerHTML = Object.keys(groups).sort().map(k => {
              const dateObj = new Date(k);
              const items = groups[k];
              return '<div class="mb-8 animate-in fade-in slide-in-from-bottom-2 duration-300">' +
                     createDateHeader(dateObj, items.length) +
                     '<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">' +
                     items.map(createListItemCard).join('') +
                     '</div></div>';
          }).join('');
      }

      // 辅助函数：创建甘特图日期列
      function createDayColumn(dayIndex) {
          const d = new Date(MIN_DATE);
          d.setDate(d.getDate() + dayIndex);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const isToday = d.getTime() === TODAY.getTime();
          const cls = isToday ? 'day-col today' : (isWeekend ? 'day-col weekend' : 'day-col');
          const weekDays = ['日','一','二','三','四','五','六'];
          return '<div class="' + cls + '"><div style="opacity:0.6">' + weekDays[d.getDay()] + '</div><div>' + (d.getMonth()+1) + '.' + d.getDate() + '</div></div>';
      }
      
      // 辅助函数：创建甘特条的天数格子
      function createBarDayCells(item, duration) {
          var cells = '';
          for (var k = 0; k < duration; k++) {
              var dayCfg = getDayConfig(item.type, k, !!item.isPredicted, item.dateObj.getTime() > TODAY.getTime());
              var roundClass = (k === 0 ? 'rounded-l-lg' : '') + (k === duration - 1 ? ' rounded-r-lg' : '');
              cells += '<div style="width:40px; flex-shrink:0;" class="flex flex-col items-center justify-center leading-none overflow-hidden ' + roundClass + ' ' + dayCfg.style + '" title="' + dayCfg.title + '"><span class="scale-90">' + dayCfg.label + '</span></div>';
          }
          return cells;
      }

      function renderGantt() {
          const container = document.getElementById('gantt-canvas');
          
          if (scheduleData.length === 0) {
              container.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#94a3b8;font-size:14px;flex-direction:column;gap:12px">' +
                  '<svg width="64" height="64" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>' +
                  '<div style="font-weight:bold;font-size:16px">暂无排班数据</div>' +
                  '<div style="font-size:12px;opacity:0.7">请先上传数据或生成预排方案</div></div>';
              return;
          }
          
          // 计算时间范围
          let maxDate = new Date(MIN_DATE);
          maxDate.setDate(maxDate.getDate() + 45);
          const lastEnd = scheduleData.reduce((max, i) => i.endTime > max ? i.endTime : max, new Date(0));
          if(lastEnd > maxDate) maxDate = new Date(lastEnd);
          maxDate.setDate(maxDate.getDate() + 5);
          const dayCount = Math.ceil((maxDate - MIN_DATE) / (1000 * 60 * 60 * 24));
          const grouped = calculateLayout();
          
          // 构建甘特图头部
          let html = '<div class="gantt-header"><div style="width:160px; flex-shrink:0; border-right:1px solid #e2e8f0; background:#f8fafc; z-index:30; position:sticky; left:0; font-weight:bold; font-size:12px; color:#64748b; display:flex; align-items:center; padding-left:16px;">资源 (老师)</div>';
          for(let i=0; i<dayCount; i++) html += createDayColumn(i);
          html += '</div>';

          grouped.forEach(group => {
              const laneHeight = 56; 
              const totalHeight = Math.max(80, group.lanes.length * laneHeight);
              html += '<div class="gantt-row" style="height:' + totalHeight + 'px">' +
                  '<div style="width:160px; flex-shrink:0; border-right:1px solid #e2e8f0; background:white; z-index:10; position:sticky; left:0; font-size:12px; font-weight:600; color:#1e293b; display:flex; flex-direction:column; justify-content:center; padding-left:16px; box-shadow: 2px 0 5px rgba(0,0,0,0.02);">' +
                      '<div>' + group.teacher + '</div>' +
                      '<div style="font-size:10px; color:#94a3b8; font-weight:normal">' + group.lanes.flat().length + ' 班次</div>' +
                  '</div>' +
                  '<div class="relative flex-1 h-full">' +
                      '<div class="absolute inset-0 flex pointer-events-none">';
              
              // 构建日期背景格子
              for(let i=0; i<dayCount; i++) {
                  const d = new Date(MIN_DATE); 
                  d.setDate(d.getDate() + i);
                  const isToday = d.getTime() === TODAY.getTime();
                  const style = isToday ? 'background:rgba(255,241,242,0.5); border-left:1px solid #fecdd3; border-right:1px solid #fecdd3;' : 'border-right:1px solid #f8fafc;';
                  html += '<div style="width:' + CELL_WIDTH + 'px; flex-shrink:0; height:100%; ' + style + '"></div>';
              }
              
              html += '</div>' +
                      group.lanes.map((lane, laneIdx) => {
                          return lane.map(item => {
                              const offsetDays = Math.round((item.dateObj - MIN_DATE) / (1000 * 60 * 60 * 24));
                              if(offsetDays < -10) return '';
                              const left = offsetDays * CELL_WIDTH;
                              const duration = item.duration || 21;
                              const INFO_WIDTH = 100; // 信息区固定宽度
                              const width = (duration * CELL_WIDTH) + INFO_WIDTH;
                              const top = 8 + (laneIdx * laneHeight);
                              
                              const isTentative = !!item.isPredicted;
                              const typeInfo = TYPE_COLORS[item.type] || { bg: 'bg-gray-500', hex: '#64748b', label: getLabel(item.type) };
                              
                              // 构建右侧信息区
                              var infoHtml = '<div class="bar-info">' +
                                  '<div class="group-code">' + (item.groupCode || '未命名') + '</div>' +
                                  '<div class="type-badge">' + typeInfo.label + '</div>' +
                                  (item.sourceSheet ? '<div class="extra-info">' + item.sourceSheet + '</div>' : '') +
                              '</div>';

                              var predClass = isTentative ? ' predicted' : ' uploaded';
                              var overlay = isTentative ? '<div class="pred-overlay"></div>' : '';

                              return '<div class="g-bar' + predClass + '" onclick="selectItem(this.getAttribute(&quot;data-id&quot;))" style="left:' + left + 'px; width:' + width + 'px; top:' + top + 'px; background:' + typeInfo.hex + ';" data-id="' + item.id + '" data-teacher="' + item.teacher + '">' +
                                           overlay + 
                                           '<div style="display:flex; z-index:2; position:relative;">' + createBarDayCells(item, duration) + '</div>' +
                                           infoHtml +
                                           '</div>';
                          }).join('');
                      }).join('') +
                  '</div>' +
              '</div>';
          });
          container.innerHTML = html;
          setupDragAndDrop();
      }

      function setupDragAndDrop() {
          const bars = document.querySelectorAll('.g-bar');
          let draggedBar = null;
          let startX = 0;
          let startLeft = 0;
          let tooltipEl = null;

          bars.forEach(bar => {
              bar.addEventListener('mousedown', (e) => {
                  e.preventDefault();
                  draggedBar = bar;
                  tooltipEl = bar.querySelector('#drag-tooltip');
                  startX = e.clientX;
                  startLeft = parseInt(bar.style.left || '0', 10);
                  bar.style.transition = 'none';
              });
          });

          document.addEventListener('mousemove', (e) => {
              if (!draggedBar) return;
              const dx = e.clientX - startX;
              const newLeft = startLeft + dx;
              draggedBar.style.left = newLeft + 'px';
              const dayIndex = Math.round(newLeft / CELL_WIDTH);
              const tempDate = new Date(MIN_DATE);
              tempDate.setDate(tempDate.getDate() + dayIndex);
              if(tooltipEl) tooltipEl.innerText = formatDate(tempDate);
          });

          document.addEventListener('mouseup', (e) => {
              if (!draggedBar) return;
              const currentLeft = parseInt(draggedBar.style.left || '0', 10);
              const dayIndex = Math.round(currentLeft / CELL_WIDTH);
              const snappedLeft = dayIndex * CELL_WIDTH;
              draggedBar.style.transition = 'left 0.2s cubic-bezier(0.2, 0, 0.2, 1)';
              draggedBar.style.left = snappedLeft + 'px';
              
              const id = draggedBar.getAttribute('data-id');
              const item = scheduleData.find(i => i.id === id);
              if (item) {
                  const newDate = new Date(MIN_DATE);
                  newDate.setDate(newDate.getDate() + dayIndex);
                  item.dateObj = newDate;
                  item.date = newDate.toISOString();
                  item.endTime = new Date(newDate.getTime() + (item.duration * 24 * 3600 * 1000));
                  const tt = draggedBar.querySelector('#drag-tooltip');
                  if(tt) tt.innerText = formatDate(newDate);
                  setTimeout(() => render(), 200);
              }
              draggedBar = null;
              tooltipEl = null;
          });
      }
      
      // 搜索功能实现
      function initSearch() {
          const searchInput = document.getElementById('search-input');
          const searchBox = document.getElementById('teacher-search');
          const resultCount = document.getElementById('search-result-count');
          
          if (!searchInput || !searchBox) return;
          
          searchInput.addEventListener('input', function(e) {
              const keyword = e.target.value.trim().toLowerCase();
              
              // 更新搜索框状态
              if (keyword) {
                  searchBox.classList.add('has-value');
              } else {
                  searchBox.classList.remove('has-value');
                  clearSearch();
                  return;
              }
              
              // 过滤老师行
              const allRows = document.querySelectorAll('.gantt-row');
              let matchCount = 0;
              
              allRows.forEach(row => {
                  const teacherNameEl = row.querySelector('div[style*="width:160px"]');
                  if (!teacherNameEl) return;
                  
                  const teacherText = teacherNameEl.textContent || '';
                  const teacherName = teacherText.split('班次')[0].trim();
                  
                  if (teacherName.toLowerCase().includes(keyword)) {
                      row.classList.remove('filtered-out');
                      matchCount++;
                      
                      // 高亮老师名字
                      const nameDiv = teacherNameEl.querySelector('div');
                      if (nameDiv && !nameDiv.querySelector('.teacher-name-highlight')) {
                          const regex = new RegExp('(' + keyword + ')', 'gi');
                          nameDiv.innerHTML = teacherName.replace(regex, '<span class="teacher-name-highlight">$1</span>');
                      }
                      
                      // 给该老师的所有排班条添加高亮动画
                      row.querySelectorAll('.g-bar').forEach(bar => {
                          bar.classList.add('search-matched');
                      });
                  } else {
                      row.classList.add('filtered-out');
                  }
              });
              
              // 显示匹配结果数量
              if (resultCount) {
                  resultCount.textContent = '找到 ' + matchCount + ' 位';
                  resultCount.style.display = matchCount > 0 ? 'block' : 'none';
              }
              
              // 自动滚动到第一个匹配项
              if (matchCount > 0) {
                  const firstMatch = document.querySelector('.gantt-row:not(.filtered-out)');
                  if (firstMatch) {
                      setTimeout(() => {
                          firstMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                  }
              }
          });
          
          // 支持 ESC 键清空搜索
          searchInput.addEventListener('keydown', function(e) {
              if (e.key === 'Escape') {
                  clearSearch();
              }
          });
      }
      
      function clearSearch() {
          const searchInput = document.getElementById('search-input');
          const searchBox = document.getElementById('teacher-search');
          const resultCount = document.getElementById('search-result-count');
          
          if (searchInput) searchInput.value = '';
          if (searchBox) searchBox.classList.remove('has-value');
          if (resultCount) resultCount.style.display = 'none';
          
          // 移除所有过滤和高亮效果
          document.querySelectorAll('.gantt-row').forEach(row => {
              row.classList.remove('filtered-out');
              
              // 恢复老师名字
              const teacherNameEl = row.querySelector('div[style*="width:160px"] > div');
              if (teacherNameEl) {
                  const highlight = teacherNameEl.querySelector('.teacher-name-highlight');
                  if (highlight) {
                      teacherNameEl.textContent = teacherNameEl.textContent;
                  }
              }
              
              // 移除排班条高亮
              row.querySelectorAll('.g-bar').forEach(bar => {
                  bar.classList.remove('search-matched');
              });
          });
      }
      
      // 双向选择与高亮：在甘特和列表中同时高亮对应项并滚动到可见区域
      function selectItem(id) {
          // clear previous
          document.querySelectorAll('.g-bar.selected').forEach(el => el.classList.remove('selected'));
          document.querySelectorAll('.list-item.selected').forEach(el => el.classList.remove('selected'));
          // highlight bar
          const bar = document.querySelector('.g-bar[data-id="' + id + '"]');
          if (bar) {
              bar.classList.add('selected');
              try { bar.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch(e){}
          }
          // highlight list
          const li = document.getElementById('list-' + id);
          if (li) {
              li.classList.add('selected');
              try { li.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(e){}
          }
      }
      render();
  <\/script>
</body>
</html>`;

  // 验证生成的 HTML 内容
  console.log('📄 HTML 生成完成:', {
    总长度: htmlContent.length,
    包含数据标记: htmlContent.includes('rawDataJson'),
    包含甘特图: htmlContent.includes('renderGantt'),
    数据片段预览: htmlContent.substring(htmlContent.indexOf('rawDataJson'), htmlContent.indexOf('rawDataJson') + 200)
  });

  if (htmlContent.length < 50000) {
    console.warn('⚠️ HTML 内容长度异常（应该 > 100KB），可能数据未注入');
  }

  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
  console.log('💾 Blob 创建完成:', {
    大小: blob.size,
    类型: blob.type
  });
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `预排决策交互报表_${new Date().toISOString().slice(0,10)}.html`;
  link.click();
  
  console.log('✅ 文件下载触发完成');
  
  URL.revokeObjectURL(url);
};